import React from "react";
import webui, { BaseComponent } from "@tinqin/tinqin-web-ui";
import EntityBuilder from "@tinqin/tinqin-web-ui/src/utils/EntityBuilder";
import ResponsiveHOC from "../../HOC/components/responsive";
import debounce from "@tinqin/tinqin-utils/src/dom/debounce";
import { getAddressFromActions } from "../../common/actions/page/PageAddress";
import { xssFilter } from "../../common/utils/xssFilter";

/**
 * The main purpose is to prevent exnanding tha main container wrapping out title, subtitle and tags
 * When we have title more than 6 lines we cut it and add dots (weblikt-line-clamp)
 * By default the subtitle is cutted up to 4 lines
 * When the title is expanded we calculate the remaining height and the subtitle number of lines we can show
 * In case we have very long title we also calculate the title number of lines we can show, and no subtitle, no tags
 * We use duplicated elements to have access to the original element height in different resolutions
 */
class ArticleBoxLongTitle extends BaseComponent {
    constructor(props) {
        super(props);
        this.state = {
            textHeight: 0,
            defaultHeight: 0,
            lineHeight: 0,
            isRestricted: false,
            showTags: true,
            descriptionLines: 4,
            titleLines: 0,
            descriptionHeight: "none",
            isTooLongTitle: false
        };
        this.restrictText = this.restrictText.bind(this);
        this.updateHeight = this.updateHeight.bind(this);
        this.onHyperlinkClick = this.onHyperlinkClick.bind(this);
        this.expandText = this.expandText.bind(this);
        this.collapseText = this.collapseText.bind(this);
        this.updateLinesCount = this.updateLinesCount.bind(this);
        this.shouldShowTags = this.shouldShowTags.bind(this);
        this.text = React.createRef();
        this.titleWrapper = React.createRef();
        this.descriptionWrapper = React.createRef();
        this.debouncedResizeListener = debounce(() => this.updateHeight(true), 200);
    }

    componentDidMount() {
        this.updateHeight();
        window.addEventListener("resize", this.debouncedResizeListener, true);
    }

    componentDidUpdate(prevProps, prevState) {
        this.shouldShowTags();
        if (prevProps.size !== this.props.size) {
            this.updateHeight(true);
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.debouncedResizeListener, true);
    }

    extractProps() {
        return webui.getUtils().extractComponentProps(this.props, {
            additionalProps: ["sizes", "restricted", "allowedLines"],
            propsDefaults: {
                sizes: [],
                restricted: false,
                allowedLines: 2
            }
        });
    }

    updateHeight(resize = false) {
        //update title and subtitle propeties
        if (this.titleWrapper && this.titleWrapper.current.children[1]) {
            const titleTextStyles = window.getComputedStyle(this.titleWrapper.current.children[1]);
            const defaultHeight = parseFloat(titleTextStyles.getPropertyValue("height"));
            const lineHeight = parseFloat(titleTextStyles.getPropertyValue("line-height"));
            const stateToSet = { defaultHeight, lineHeight };
            const container = this.titleWrapper.current.parentElement.parentElement;
            //In case a window resize event is triggered we set main container height to auto,
            //to be able to get the right dimensions depending on the content
            if (resize) {
                container.style.height = "auto";
            }
            if (this.descriptionWrapper.current && this.descriptionWrapper.current.children[1]) {
                const descriptionStyles = window.getComputedStyle(
                    this.descriptionWrapper.current.children[1]
                );
                stateToSet.descriptionHeight = parseFloat(
                    descriptionStyles.getPropertyValue("height")
                );
            }
            this.setState(stateToSet, this.restrictText);
            this.shouldShowTags();
        }
    }

    restrictText() {
        const props = this.extractProps();
        if (
            this.text.current &&
            props.restricted &&
            props.sizes &&
            props.sizes.includes(this.props.size)
        ) {
            const textHeight = this.state.lineHeight * props.allowedLines;
            if (textHeight < this.state.defaultHeight) {
                this.setState(
                    {
                        textHeight: textHeight,
                        isRestricted: true
                    },
                    this.updateLinesCount
                );
                return;
            }
        }
        this.setState(
            {
                isRestricted: false,
                showTags: true
            },
            this.updateLinesCount
        );
    }

    onHyperlinkClick = event => {
        event.preventDefault();
        const titleConfig = this.props.config.children.find(child =>
            child.bindTo.endsWith("title")
        );
        const { actions } = this.extractDataProps();
        if (titleConfig && titleConfig.actions?.length) {
            actions.push(...titleConfig.actions);
        }
        if (actions.length) {
            return this.props.dispatchSirenActions(
                [...actions],
                this.props.widgetId,
                this.props.computedBindTo + ".title",
                ["default"]
            );
        }
    };

    expandText(e) {
        if (this.state.isRestricted) {
            //On title expand we need to prevent container expanding, so we fix the height
            if (this.titleWrapper && this.titleWrapper.current) {
                const container = this.titleWrapper.current.parentElement.parentElement;
                container.style.height = container.offsetHeight + "px";
            }
            this.setState(
                {
                    isRestricted: false,
                    showTags: false
                },
                this.updateLinesCount
            );
        }
    }
    collapseText(e) {
        this.updateHeight();
    }

    extractDataProps() {
        const data = this.props.data || {};
        const siren = EntityBuilder.of(data);
        const description = siren.getChild("description").getValue();
        const title = siren.getChild("title").getValue();
        const tags = siren.getChild("tags").getChildren();
        const actions = siren.getChild("title").getActions();

        return { title, description, actions, tags };
    }

    shouldShowTags() {
        if (this.titleWrapper.current) {
            const props = this.extractProps();
            const container = this.titleWrapper.current.children[1];
            const titleLines = Math.floor(container.offsetHeight / this.state.lineHeight);
            if (titleLines === props.allowedLines) {
                this.setState({ showTags: false });
            }
        }
    }

    updateLinesCount() {
        //Calculates the space left in the container and how many lines from the subtitle could be visible
        if (this.descriptionWrapper.current) {
            const descriptionBox = this.descriptionWrapper.current.children[1];
            const container = descriptionBox.parentElement.parentElement;
            const descriptionStyles = window.getComputedStyle(descriptionBox.children[0]);
            const titleStyles = window.getComputedStyle(this.titleWrapper.current);
            const lineHeight = parseFloat(descriptionStyles.getPropertyValue("line-height"));
            const titleMarginBottom = parseFloat(titleStyles.getPropertyValue("margin-bottom"));
            const titleHeight = this.state.isRestricted
                ? this.state.textHeight
                : this.titleWrapper.current.scrollHeight;
            const spaceLeft = container.offsetHeight - (titleMarginBottom + titleHeight);
            let descriptionLines = Math.floor(spaceLeft / lineHeight);
            //Max visible subtitle lines are 4
            descriptionLines = descriptionLines < 0 ? 0 : Math.min(descriptionLines, 4);
            const descriptionHeight = lineHeight * descriptionLines;

            let titleLines = this.props.allowedLines;
            let isTooLongTitle = false;
            //Check if the title is longer than the main container, if so we calculates title visible lines
            if (this.state.defaultHeight > container.offsetHeight) {
                isTooLongTitle = true;
                titleLines = Math.floor(container.offsetHeight / this.state.lineHeight);
            }
            this.setState({
                descriptionHeight,
                descriptionLines,
                titleLines,
                isTooLongTitle
            });
        }
    }

    render() {
        let { title, description } = this.extractDataProps();
        const { actions, tags } = this.extractDataProps();
        const props = this.extractProps();
        if (title) {
            title = title.replaceAll(/<p>|<\/p>/g, "");
        }
        if (description) {
            description = description.replaceAll(/<p>|<\/p>/g, "");
        }
        const isRestrictedClass = !this.state.isRestricted ? "un-reveal-title" : "";
        const shouldShowTags = !!tags.length && !this.state.isRestricted && this.state.showTags;
        let descriptionHeight = this.state.descriptionHeight;
        if (descriptionHeight !== "none") {
            descriptionHeight += "px";
        }

        const titleStyles = this.state.isRestricted
            ? {
                  maxHeight: this.state.textHeight + "px",
                  WebkitLineClamp: props.allowedLines
              }
            : this.state.isTooLongTitle
            ? { WebkitLineClamp: this.state.titleLines }
            : {};

        return (
            <div
                className="tq-flex-item un-flex-no-mt"
                onMouseEnter={this.expandText}
                onMouseLeave={this.collapseText}
            >
                {title && (
                    <a
                        href={getAddressFromActions(actions)}
                        className="un-anchor-custom"
                        onClick={this.onHyperlinkClick}
                    >
                        <h3
                            className="tq-title tq-clickable tq-bold tq-main-color un-title-no-mt"
                            ref={this.titleWrapper}
                        >
                            <span
                                ref={this.text}
                                style={titleStyles}
                                className={`tq-text-node ${isRestrictedClass}`}
                                dangerouslySetInnerHTML={{
                                    __html: xssFilter.process(title)
                                }}
                            ></span>
                            <span
                                className="tq-text-node tq-hide-in-1024 un-hidden-title"
                                dangerouslySetInnerHTML={{
                                    __html: xssFilter.process(title)
                                }}
                            ></span>
                        </h3>
                    </a>
                )}
                {description && (
                    <div
                        className="un-article-content-wrapper"
                        style={{ maxHeight: descriptionHeight }}
                        ref={this.descriptionWrapper}
                    >
                        <div className="un-article-content tq-offset-bottom">
                            <p
                                style={{ WebkitLineClamp: this.state.descriptionLines }}
                                dangerouslySetInnerHTML={{
                                    __html: xssFilter.process(description)
                                }}
                            ></p>
                        </div>
                        <div className="un-article-content un-hidden-subtitle">
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: xssFilter.process(description)
                                }}
                            ></p>
                        </div>
                    </div>
                )}
                {shouldShowTags && (
                    <div className="un-badges-wrapper">
                        {tags.map(tag => {
                            return (
                                <div className="un-badges-box" key={tag.getId()}>
                                    <span>{tag.getValue()}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }
}

export default webui.connectComponent({
    actions: {
        dispatchSirenActions: "siren.dispatchSirenActions"
    }
})(ResponsiveHOC(ArticleBoxLongTitle));
